import {
  ApiBlueprint,
  analyticsApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import type {
  AnalyticsApi,
  AnalyticsEvent,
} from '@backstage/frontend-plugin-api';
import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

type QueuedEvent = {
  action: string;
  subject?: string;
  plugin?: string;
  route?: string;
  /** Sign-in provider, set on the per-app-load session event only. */
  provider?: string;
  /** Entity ref, set on entity page views (feeds "Popular services"). */
  entity?: string;
};

// Catalog entity pages: /catalog/:namespace/:kind/:name[/...]
const ENTITY_PATH_PATTERN = /^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)/;

const entityRefFromPath = (path: string) => {
  const match = path.match(ENTITY_PATH_PATTERN);
  return match
    ? `${match[2]}:${match[1]}/${match[3]}`.toLocaleLowerCase('en-US')
    : undefined;
};

const signInProvider = () => {
  try {
    return (
      localStorage.getItem('savr.signInProvider') ??
      (process.env.NODE_ENV === 'development' ? 'guest' : 'unknown')
    );
  } catch {
    return 'unknown';
  }
};

const FLUSH_INTERVAL_MS = 15_000;
const MAX_BATCH = 50;

/**
 * Forwards Backstage's built-in analytics events (navigate, click, search,
 * ... emitted by core and plugins) plus one `session` event per app load to
 * the savr-analytics backend, which aggregates them into savr.backstage.*
 * Datadog metrics. Events are batched and flushed every 15s (and on page
 * hide); failures are dropped silently - analytics must never break the app.
 */
export class DatadogAnalyticsApi implements AnalyticsApi {
  private queue: QueuedEvent[] = [];
  private timer: number | undefined;

  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {
    this.queue.push({ action: 'session', provider: signInProvider() });
    this.schedule();
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });
  }

  captureEvent(event: AnalyticsEvent) {
    const context = event.context as Record<string, unknown>;
    this.queue.push({
      action: event.action,
      subject: event.subject?.slice(0, 100) || undefined,
      plugin: (context.pluginId as string) || undefined,
      route:
        (context.routeRef as string) ||
        (context.extensionId as string) ||
        undefined,
      // navigate subjects are pathnames; tag entity page views by ref.
      entity:
        event.action === 'navigate' && event.subject
          ? entityRefFromPath(event.subject)
          : undefined,
    });
    if (this.queue.length >= MAX_BATCH) {
      this.flush();
    } else {
      this.schedule();
    }
  }

  private schedule() {
    if (this.timer === undefined) {
      this.timer = window.setTimeout(() => this.flush(), FLUSH_INTERVAL_MS);
    }
  }

  private async flush() {
    if (this.timer !== undefined) {
      window.clearTimeout(this.timer);
      this.timer = undefined;
    }
    const events = this.queue.splice(0, MAX_BATCH);
    if (events.length === 0) {
      return;
    }
    try {
      const base = await this.discoveryApi.getBaseUrl('savr-analytics');
      await this.fetchApi.fetch(`${base}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
      });
    } catch {
      /* dropped - never surface analytics errors */
    }
    if (this.queue.length > 0) {
      this.schedule();
    }
  }
}

// Replaces the app's no-op analytics api (same apiRef -> same extension id).
export const datadogAnalyticsApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams({
      api: analyticsApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new DatadogAnalyticsApi(discoveryApi, fetchApi),
    }),
});
